<template>
    <div class="relative date-range-picker">
        <div class="relative">
            <input
                type="text"
                :value="formatDateRange"
                readonly
                placeholder="Seleccionar rango de fechas"
                class="w-72 px-4 py-2 border rounded-lg cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
                @click="toggleCalendar"
            />
            <span class="absolute right-3 top-2 text-gray-500">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
            </span>
        </div>

        <!-- Popup -->
        <div v-if="showCalendar" class="absolute mt-2 bg-white rounded-lg shadow-lg border p-4 z-50">
            <div class="flex justify-between items-center mb-4">
                <button @click="previousMonth" class="p-1 hover:bg-gray-100 rounded">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                    </svg>
                </button>
                <span class="font-semibold">{{ currentMonthName }} {{ currentYear }}</span>
                <button @click="nextMonth" class="p-1 hover:bg-gray-100 rounded">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg>
                </button>
            </div>

            <div class="grid grid-cols-7 gap-1">
                <div v-for="day in weekDays" :key="day" class="text-center text-sm font-semibold text-gray-600">
                    {{ day }}
                </div>


                <div
                    v-for="date in calendarDays"
                    :key="date.toISOString()"
                    @click="selectDate(date)"
                    :class="[
                        'text-center p-2 cursor-pointer rounded hover:bg-gray-100',
                        isSelected(date) ? 'bg-green-500 text-white hover:bg-green-600' : '',
                        isInRange(date) ? 'bg-green-100' : '',
                        date.getMonth() !== currentMonth ? 'text-gray-400' : ''
                    ]"
                >
                    {{ date.getDate() }}
                </div>
            </div>

            <div class="mt-4 flex justify-end space-x-2">
                <button
                    @click="clearDates"
                    class="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded"
                >
                    Limpiar
                </button>
                <button
                    @click="applyDates"
                    class="px-4 py-2 text-sm bg-green-500 text-white rounded hover:bg-green-600"
                >
                    Aplicar
                </button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, defineEmits } from 'vue';

const emit = defineEmits(['update:dateRange']);

const showCalendar = ref(false);
const currentDate = ref(new Date());
const startDate = ref<Date | null>(null);
const endDate = ref<Date | null>(null);
const selecting = ref<'start' | 'end'>('start');

const currentYear = computed(() => currentDate.value.getFullYear());
const currentMonth = computed(() => currentDate.value.getMonth());
const currentMonthName = computed(() => {
    return new Intl.DateTimeFormat('es-ES', { month: 'long' }).format(currentDate.value);
});

const weekDays = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

const calendarDays = computed(() => {
    const days: Date[] = [];
    const firstDay = new Date(currentYear.value, currentMonth.value, 1);
    const lastDay = new Date(currentYear.value, currentMonth.value + 1, 0);
    
    const firstDayOfWeek = firstDay.getDay();
    for (let i = firstDayOfWeek; i > 0; i--) {
        days.push(new Date(currentYear.value, currentMonth.value, -i + 1));
    }
    
    for (let i = 1; i <= lastDay.getDate(); i++) {
        days.push(new Date(currentYear.value, currentMonth.value, i));
    }
    
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
        days.push(new Date(currentYear.value, currentMonth.value + 1, i));
    }
    
    return days;
});

const formatDateRange = computed(() => {
    if (!startDate.value && !endDate.value) {
        return '';
    }

    if (startDate.value && !endDate.value) {
        return `Desde ${formatDate(startDate.value)}`;
    }

    if (startDate.value && endDate.value) {
        return `${formatDate(startDate.value)} - ${formatDate(endDate.value)}`;
    }

    return '';
});

const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    }).format(date);
};

const toggleCalendar = () => {
    showCalendar.value = !showCalendar.value;
};

const previousMonth = () => {
    currentDate.value = new Date(currentYear.value, currentMonth.value - 1);
};

const nextMonth = () => {
    currentDate.value = new Date(currentYear.value, currentMonth.value + 1);
};

const selectDate = (date: Date) => {
    if (selecting.value === 'start' || !startDate.value) {
        startDate.value = new Date(date);
        selecting.value = 'end';
        if (endDate.value && startDate.value > endDate.value) {
            endDate.value = null;
        }
    } else {
        if (date < startDate.value) {
            endDate.value = startDate.value;
            startDate.value = new Date(date);
        } else {
            endDate.value = new Date(date);
        }
        selecting.value = 'start';
    }
};

const isSelected = (date: Date) => {
    return (
        startDate.value?.toDateString() === date.toDateString() ||
        endDate.value?.toDateString() === date.toDateString()
    );
};

const isInRange = (date: Date) => {
    if (!startDate.value || !endDate.value) return false;
    return date > startDate.value && date < endDate.value;
};

const clearDates = () => {
    startDate.value = null;
    endDate.value = null;
    selecting.value = 'start';
    emit('update:dateRange', {
        startDate: null,
        endDate: null
    });
    showCalendar.value = false;
};

const applyDates = () => {
    emit('update:dateRange', {
        startDate: startDate.value,
        endDate: endDate.value
    });
    showCalendar.value = false;
};

const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (!target.closest('.date-range-picker')) {
        showCalendar.value = false;
    }
};

onMounted(() => {
    document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
.date-range-picker {
    position: relative;
    z-index: 50;
}

input::placeholder {
    color: #9CA3AF;
}

.calendar-enter-active,
.calendar-leave-active {
    transition: opacity 0.2s ease;
}

.calendar-enter-from,
.calendar-leave-to {
    opacity: 0;
}
</style> 